import { SettlementKeys, SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'
import { PayPeriodStatus } from '@/utils/Enumerations.js'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'

export default {
  setDateConfiguration (state, dateConfiguration) {
    Object.values(SettlementSubKeys.AP).forEach((value) => {
      state[SettlementKeys.AccountsPayable][value] = undefined
    })
    Object.values(SettlementSubKeys.AR).forEach((value) => {
      state[SettlementKeys.AccountsReceivable][value] = undefined
    })
    Object.values(SettlementSubKeys.AC).forEach((value) => {
      state[SettlementKeys.Accruals][value] = undefined
    })

    state.dateConfiguration = dateConfiguration
  },

  resetModules (state) {
    Object.values(SettlementSubKeys.AP).forEach((value) => {
      state[SettlementKeys.AccountsPayable][value] = undefined
    })
    Object.values(SettlementSubKeys.AR).forEach((value) => {
      state[SettlementKeys.AccountsReceivable][value] = undefined
    })
    Object.values(SettlementSubKeys.AC).forEach((value) => {
      state[SettlementKeys.Accruals][value] = undefined
    })

    state.dateConfiguration = {
      payPeriod: undefined,
      fiscalYear: undefined
    }
  },

  setModuleData (state, moduleData) {
    state[moduleData.moduleKey][moduleData.subModuleKey] = moduleData.data
  },

  setCurrentModule (state, moduleInformation) {
    state.currentModuleKey = moduleInformation.moduleKey
    state.currentSubModuleKey = moduleInformation.subModuleKey
  },

  setLoading (state, loading) {
    state.loading = loading
  },

  setUseMetricTons (state, useMetricTons) {
    state.useMetricTons = useMetricTons
    localStorage.setItem(LocalStorageKeys.SETTLEMENTS_USE_METRIC_TONS, useMetricTons)
  },

  setUseByproductMetricTons (state, useByproductMetricTons) {
    state.useByproductMetricTons = useByproductMetricTons
    localStorage.setItem(LocalStorageKeys.SETTLEMENTS_USE_BYPRODUCT_METRIC_TONS, useByproductMetricTons)
  },

  setARSalesAnalysisBusinessEntity (state, arSalesAnalysisBusinessEntity) {
    state.arSalesAnalysisBusinessEntity = arSalesAnalysisBusinessEntity
  },
  setTractPayments: (state, tractPayments) => { state.tractPayments = tractPayments },

  toggleContractStatus (state, contract) {
    const currentPayPeriod = state.dateConfiguration.payPeriod
    const savedContract = state.settlementContractStatuses[currentPayPeriod.payPeriodId][contract.contractId]
    let newVerifiedStatus = savedContract.verified === 0 ? 1 : 0

    if (savedContract.amt !== savedContract.oldAmt) {
      newVerifiedStatus = 1
    }

    state.settlementContractStatuses[currentPayPeriod.payPeriodId][contract.contractId] = {
      amt: savedContract.amt,
      oldAmt: savedContract.amt,
      verified: newVerifiedStatus
    }
    persistStatus.byContract.write(state.settlementContractStatuses)
  },

  initializeContractStatuses (state, { settlements, payPeriods }) {
    const currentPayPeriod = state.dateConfiguration.payPeriod

    let statuses = {}

    if (!persistStatus.byContract.has()) {
      statuses[currentPayPeriod.payPeriodId] = settlements.reduce((contractStatuses, { contractInfo }) => {
        contractStatuses[contractInfo.contractId] = {
          amt: contractInfo.amount,
          oldAmt: contractInfo.amount,
          verified: 0
        }
        return contractStatuses
      }, {})
    } else {
      statuses = persistStatus.byContract.read()
      payPeriods.filter(payPeriod => payPeriod.status === PayPeriodStatus.Exported.value).forEach(payPeriod => {
        delete statuses[payPeriod.payPeriodId]
      })

      if (!statuses[currentPayPeriod.payPeriodId]) {
        statuses[currentPayPeriod.payPeriodId] = settlements.reduce((contractStatuses, { contractInfo }) => {
          contractStatuses[contractInfo.contractId] = {
            amt: contractInfo.amount,
            oldAmt: contractInfo.amount,
            verified: 0
          }
          return contractStatuses
        }, {})
      } else {
        settlements.forEach(({ contractInfo }) => {
          const contractFromCookie = statuses[currentPayPeriod.payPeriodId][contractInfo.contractId]
          statuses[currentPayPeriod.payPeriodId][contractInfo.contractId] = {
            amt: contractInfo.amount,
            oldAmt: contractFromCookie ? contractFromCookie.oldAmt : contractInfo.amount,
            verified: contractFromCookie ? contractFromCookie.verified ?? 0 : 0
          }
        })
      }
    }

    state.settlementContractStatuses = statuses
    persistStatus.byContract.write(state.settlementContractStatuses)
  },

  clearStaleSubModuleData (state, { isByproduct = false, allData = false }) {
    const volatileLogsKeys = {
      accountsPayable: [
        SettlementSubKeys.AP.SettlementsByContract,
        SettlementSubKeys.AP.PaymentRegister,
        SettlementSubKeys.AP.ProductionSummary,
        SettlementSubKeys.AP.TicketRegister,
        SettlementSubKeys.AP.SettlementsByPayee,
        SettlementSubKeys.AP.MiscPayments // Includes advances and account payments
      ],
      accountsReceivable: [
        SettlementSubKeys.AR.ReceivableRegister,
        SettlementSubKeys.AR.ProductionSummary,
        SettlementSubKeys.AR.SalesAnalysis
      ],
      accruals: [
        SettlementSubKeys.AC.Register,
        SettlementSubKeys.AC.MiscAccruals // Includes Accrual Account Payments
      ]
    }

    const volatileByproductsKeys = {
      accountsPayable: [
        SettlementSubKeys.AP.BPPaymentRegister,
        SettlementSubKeys.AP.ByproductSettlementsByPayee
      ],
      accountsReceivable: [
        SettlementSubKeys.AR.BPReceivableRegister,
        SettlementSubKeys.AR.BPReceivablesByPayer,
        SettlementSubKeys.AR.BPReceivableSummary
      ],
      accruals: [
        SettlementSubKeys.AC.BPRegister
      ]
    }
    if (!allData) {
      const volatileKeysByContractType = isByproduct ? volatileByproductsKeys : volatileLogsKeys
      const keys = Object.keys(volatileKeysByContractType)
      keys.forEach(key => {
        const settlementSubKeys = volatileKeysByContractType[key]
        settlementSubKeys.forEach(subModuleKey => {
          if (state.currentSubModuleKey !== subModuleKey) {
            state[key][subModuleKey] = undefined
          }
        })
      })
    } else {
      Object.values(SettlementSubKeys.AP).forEach((value) => {
        state[SettlementKeys.AccountsPayable][value] = undefined
      })
      Object.values(SettlementSubKeys.AR).forEach((value) => {
        state[SettlementKeys.AccountsReceivable][value] = undefined
      })
      Object.values(SettlementSubKeys.AC).forEach((value) => {
        state[SettlementKeys.Accruals][value] = undefined
      })
    }
  }
}

const persistStatus = {
  byContract: {
    read () {
      const v = localStorage.getItem(LocalStorageKeys.SETTLEMENT_CONTRACT_STATUSES)
      return v ? JSON.parse(v) : undefined
    },
    write (statuses) {
      localStorage.setItem(LocalStorageKeys.SETTLEMENT_CONTRACT_STATUSES, JSON.stringify(statuses))
    },
    has () {
      return localStorage.getItem(LocalStorageKeys.SETTLEMENT_CONTRACT_STATUSES) !== null
    }
  }
}
